// ============================================================================
// PlanificationPage — grille de planification salariale director (L05, F-601..611)
// ============================================================================
// SHELL de la page (SL-A) : breadcrumb (F-605), PageHeader, selecteur d'equipe
// (M-652 : portee deja filtree serveur), garde CLIENT Forbidden (M-651), bandeaux
// ?ok=/?error= (deep-links F-604), skeleton/QueryError. La GRILLE interactive
// (DirectorGridBoard vue director + EmployeeGrid vue employe) arrive en SL-B : ici
// un EmptyState « a venir » tient la place ou la grille se branchera.
//
// Portage du PoC (dashboard)/planification-salariale/page.tsx (SSR searchParams)
// au standard maison (SPA + useSearchParams). Gabarit : MonEquipePage (garde
// director-scoped, selecteur GsSegmented) + CommitteeDetailPage (fil d'Ariane).
//
// Garde M-651 : la garde reelle est le 403 SERVEUR (manager ET employee refuses
// en amont de la reponse). Le SPA ne DECIDE pas — il redirige le 403 vers l'ecran
// d'erreur standard /error/403, EN PLUS de la garde serveur (lecon GA-05 : jamais
// recalculer l'autorisation cote client).

import { Link, Navigate, useSearchParams } from 'react-router-dom';
import {
  PageHeader, EmptyState, GsSegmented, SkeletonCard, QueryError,
} from '@gs/ui';
import { ChevronRight, LineChart, Users } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { resolvePlanningBack } from '../../lib/nav/back-context';
import { usePlanification } from '../../hooks/usePlanification';

export default function PlanificationPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // F-603/604/605 : deep-links. Le SERVEUR decide si `team` est dans la portee
  // (un id hors portee retombe sur la 1re equipe, jamais honore ici).
  const teamParam = searchParams.get('team');
  const okCode = searchParams.get('ok'); // draft_saved / submitted / position_*
  const errorCode = searchParams.get('error'); // forbidden / invalid / notfound / server
  const back = resolvePlanningBack({
    from: searchParams.get('from') ?? undefined,
    member: searchParams.get('member') ?? undefined,
    team: teamParam ?? undefined,
  });

  const q = usePlanification(teamParam, !!user);

  // Garde CLIENT M-651 : le 403 serveur (manager/employee) -> ecran d'erreur
  // standard. La redirection ne REMPLACE pas la garde serveur, elle la double.
  if (q.isError && q.error instanceof ApiForbiddenError) {
    return <Navigate to="/error/403" replace />;
  }

  function selectTeam(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set('team', id);
    next.delete('ok');
    next.delete('error');
    setSearchParams(next);
  }

  const data = q.data;
  const selectedTeam = data?.teams.find((team) => team.id === data.selectedTeamId) ?? null;

  return (
    <div className="space-y-5">
      {/* F-605 : fil d'Ariane de retour contextuel — href whiteliste (anti
          open-redirect, resolvePlanningBack), libelle bilingue par cle i18n. */}
      {back ? (
        <nav
          aria-label={t('gest:planification.breadcrumbLabel')}
          className="mb-2 flex items-center gap-1.5 text-meta text-slate-500"
        >
          <Link
            to={back.href}
            className="touch-manipulation font-medium text-slate-500 transition-colors hover:text-slate-700 dark:hover:text-slate-300"
          >
            {t(back.labelKey)}
          </Link>
          <ChevronRight size={14} aria-hidden className="text-slate-300 dark:text-slate-600" />
          <span aria-current="page" className="truncate font-semibold text-slate-800 dark:text-slate-100">
            {t('gest:planification.title')}
          </span>
        </nav>
      ) : null}

      <PageHeader
        title={t('gest:planification.title')}
        description={selectedTeam?.name}
        onRefresh={() => void q.refetch()}
        refreshLoading={q.isFetching}
      />

      {q.isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : q.isError ? (
        <QueryError message={t('gest:planification.loadError')} onRetry={() => void q.refetch()} />
      ) : data ? (
        <>
          {/* F-604 : bandeau ?ok= (succes deep-linkable) — liseré vert SM-5. */}
          {okCode ? (
            <div
              role="status"
              className="rounded-xl border-l-4 border-green-500 bg-green-50 p-4 text-field text-green-700 dark:bg-green-950 dark:text-green-300"
            >
              {t(`gest:planification.ok.${okCode}`, { defaultValue: t('gest:planification.ok.draft_saved') })}
            </div>
          ) : null}

          {/* F-604 : bandeau ?error= mappe i18n (GA-21 : jamais un code brut). */}
          {errorCode ? (
            <div
              role="alert"
              className="rounded-xl border-l-4 border-red-500 bg-red-50 p-4 text-field text-red-700 dark:bg-red-950 dark:text-red-300"
            >
              {t(`gest:planification.errors.${errorCode}`, { defaultValue: t('gest:planification.errors.server') })}
            </div>
          ) : null}

          {/* M-652 : selecteur multi-equipes (visible si >1 equipe dans la PORTEE serveur). */}
          {data.teams.length > 1 ? (
            <GsSegmented
              options={data.teams.map((team) => ({ value: team.id, label: team.name }))}
              value={data.selectedTeamId ?? data.teams[0].id}
              onChange={selectTeam}
              ariaLabel={t('gest:planification.teamSelector')}
              variant="scrollable"
            />
          ) : null}

          {!selectedTeam ? (
            // Aucune equipe dans la portee -> EmptyState (pas une erreur).
            <EmptyState
              icon={<Users size={48} aria-hidden="true" />}
              title={t('gest:planification.empty.title')}
              description={t('gest:planification.empty.description')}
            />
          ) : (
            // SL-A : la grille interactive (DirectorGridBoard/EmployeeGrid) se
            // branchera ICI en SL-B. Le shell compile et navigue des maintenant.
            <EmptyState
              icon={<LineChart size={48} aria-hidden="true" />}
              title={t('gest:planification.comingSoon.title')}
              description={t('gest:planification.comingSoon.description')}
            />
          )}
        </>
      ) : null}
    </div>
  );
}
